import { computed, effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FeatureFlags, LaunchDarklyService } from '@lc/core/lib/services/launch-darkly.service';
import { CookieService } from '@lc/core/lib/services/cookie.service';
import { UserSettingsService } from '@lc/core/lib/services/user-settings.service';
import { AppService } from './app.service';
import { UserService } from './user.service';
import { BrandCode } from '../models';

export interface BrandLogo {
  src: string;
  width: string;
  height: string;
}

interface BrandLogoScheme {
  light: string;
  dark?: string;
  width: string;
  height: string;
}

interface SupportContact {
  email: string;
  phoneNumber: string;
}

export interface BrandData {
  name: string;
  brandKey: string;
  mdmBrandCodes: string[];
  headerLogoImage: BrandLogoScheme;
  footerLogoImage: string;
  footerWideLogoImage: string;
  facebookLink: string;
  twitterLink: string;
  instagramLink: string;
  youtubeLink: string;
  termsOfUseLink?: string;
  helpdeskLabel: string;
  helpdeskPhone: string;
  helpdeskPhoneHint: string | null;
  helpdeskEmail: string | null;
  copyright: string;
}

const logos: Record<string, BrandLogoScheme> = {
  theStudio: {
    light: '/assets/images/the-studio-light.png',
    dark: '/assets/images/the-studio-dark.png',
    width: '9000',
    height: '1904',
  },
  CBA: {
    light: '/assets/images/cb-logo.png',
    width: '241',
    height: '241',
  },
  CBR: {
    light: '/assets/images/cb-logo.png',
    width: '241',
    height: '241',
  },
  ERA: {
    light: '/assets/images/era-logo.svg',
    width: '91',
    height: '112',
  },
  BHG: {
    light: '/assets/images/bhg-logo.png',
    width: '600',
    height: '594',
  },
  C21: {
    light: '/assets/images/c21-logo.png',
    width: '1541',
    height: '1963',
  },
  CMP: {
    light: '/assets/images/compass.svg',
    width: '112',
    height: '16',
  },
  CIH: {
    light: '/assets/images/cih.svg',
    dark: '/assets/images/cih_dark.svg',
    width: '112',
    height: '16',
  },
};

const year = new Date().getFullYear();
const brandData = {
  CBR: {
    name: 'Coldwell Banker',
    brandKey: 'cb',
    mdmBrandCodes: ['CBR'],
    headerLogoImage: logos.CBR,
    footerLogoImage: 'https://media.concierge-suite.com/artifacts/brands/cb-northstar-logo-stacked.png',
    footerWideLogoImage:
      'https://media.concierge-suite.com/artifacts/brands/CB_Logo_Horizontal_RGB_2.svg',
    facebookLink: 'https://www.facebook.com/coldwellbanker',
    twitterLink: 'https://twitter.com/coldwellbanker',
    instagramLink: 'https://www.instagram.com/coldwellbanker',
    youtubeLink: 'https://www.youtube.com/user/coldwellbanker',
    helpdeskLabel: 'Help Desk',
    helpdeskPhone: '877-426-5393',
    helpdeskPhoneHint: null,
    helpdeskEmail: null,
    copyright:
      `© ${year} Coldwell Banker. All Rights Reserved. Coldwell Banker and the Coldwell Banker logos are trademarks of Coldwell Banker Real Estate LLC. The Coldwell Banker® System is comprised of company owned offices which are owned by a subsidiary of Realogy Brokerage Group LLC and franchised offices which are independently owned and operated. The Coldwell Banker System fully supports the principles of the Fair Housing Act and the Equal Opportunity Act.`,
  },
  CBA: {
    name: 'Coldwell Banker',
    brandKey: 'cb',
    mdmBrandCodes: ['CBR'],
    headerLogoImage: logos.CBA,
    footerLogoImage: 'https://fs.rsc.realogy.com/media/brands/1/cb-northstar-logo-stacked.png',
    footerWideLogoImage:
      'https://fs.rsc.realogy.com/media/brands/1/CB_Logo_Horizontal_RGB_2.svg',
    facebookLink: 'https://www.facebook.com/coldwellbanker',
    twitterLink: 'https://twitter.com/coldwellbanker',
    instagramLink: 'https://www.instagram.com/coldwellbanker',
    youtubeLink: 'https://www.youtube.com/user/coldwellbanker',
    helpdeskLabel: 'Help Desk',
    helpdeskPhone: '877-426-5393',
    helpdeskPhoneHint: null,
    helpdeskEmail: null,
    copyright:
      `© ${year} Coldwell Banker. All Rights Reserved. Coldwell Banker and the Coldwell Banker logos are trademarks of Coldwell Banker Real Estate LLC. The Coldwell Banker® System is comprised of company owned offices which are owned by a subsidiary of Realogy Brokerage Group LLC and franchised offices which are independently owned and operated. The Coldwell Banker System fully supports the principles of the Fair Housing Act and the Equal Opportunity Act.`,
  },
  ERA: {
    name: 'ERA',
    brandKey: 'era',
    mdmBrandCodes: ['ERA'],
    headerLogoImage: logos.ERA,
    footerLogoImage: 'https://media.concierge-suite.com/artifacts/brands/era-header-icon.svg',
    footerWideLogoImage: 'https://media.concierge-suite.com/artifacts/brands/era-header-icon.svg',
    facebookLink: 'https://www.facebook.com/erarealestate/',
    twitterLink: 'https://twitter.com/ASmarterERA',
    instagramLink: 'https://instagram.com/erarealestate',
    youtubeLink: 'https://www.youtube.com/user/era',
    helpdeskLabel: 'Help Desk',
    helpdeskPhone: '888-435-7437',
    helpdeskPhoneHint: null,
    helpdeskEmail: 'erasupport@teamera.com',
    copyright: `© ${year} ERA Franchise Systems LLC. All rights reserved.`,
  },
  BHG: {
    name: 'Better Homes and Gardens Real Estate',
    brandKey: 'bhg',
    mdmBrandCodes: ['BHG'],
    headerLogoImage: logos.BHG,
    footerLogoImage: 'https://media.concierge-suite.com/artifacts/brands/BHGRE-Footer.png',
    footerWideLogoImage: 'https://media.concierge-suite.com/artifacts/brands/BHGRE-Footer.png',
    facebookLink: 'https://facebook.com/BHGRealEstate',
    twitterLink: 'http://twitter.com/BHGRealEstate',
    instagramLink: 'https://www.instagram.com/bhgrealestate/',
    youtubeLink: 'http://youtube.com/BHGRealEstate',
    helpdeskLabel: 'Help Desk',
    helpdeskPhone: '866-616-4244',
    helpdeskPhoneHint: null,
    helpdeskEmail: 'Help@bhgre.com',
    copyright: `© ${year} BHGRE. All rights reserved.`,
  },
  C21: {
    name: 'Century 21',
    brandKey: 'c21',
    mdmBrandCodes: ['C21'],
    headerLogoImage: logos.C21,
    footerLogoImage: 'https://media.concierge-suite.com/artifacts/brands/c21-footerlogo.png',
    footerWideLogoImage: 'https://media.concierge-suite.com/artifacts/brands/c21-footerlogo.png',
    facebookLink: 'https://www.facebook.com/century21/',
    instagramLink: 'https://www.instagram.com/century21/',
    youtubeLink: 'https://www.youtube.com/century21',
    twitterLink: '',
    termsOfUseLink: 'https://www.century21.com/general/terms-of-use/',
    helpdeskLabel: 'Help Desk',
    helpdeskPhone: '877-221-2765',
    helpdeskPhoneHint: null,
    helpdeskEmail: '',
    copyright: `© ${year} Century 21 Real Estate LLC. All rights reserved. `,
  },
  CMP: {
    name: 'COMPASS',
    brandKey: 'cmp',
    mdmBrandCodes: ['CMP'],
    headerLogoImage: logos.CMP,
    footerLogoImage: 'https://media.concierge-suite.com/artifacts/brands/compass.svg',
    footerWideLogoImage: 'https://media.concierge-suite.com/artifacts/brands/compass.svg',
    facebookLink: '',
    instagramLink: '',
    youtubeLink: '',
    twitterLink: '',
    helpdeskLabel: '',
    helpdeskPhone: '',
    helpdeskPhoneHint: null,
    helpdeskEmail: '',
    copyright: `© ${year} COMPASS, the Compass logo, and other various trademarks, logos, designs, and slogans are the registered and unregistered trademarks of Compass, Inc. dba Compass in the U.S. and/or other countries. `,
  },
  CIH: {
    name: 'COMPASS International Holdings ',
    brandKey: 'cih',
    mdmBrandCodes: ['CIH'],
    headerLogoImage: logos.CIH,
    footerLogoImage: 'https://media.concierge-suite.com/artifacts/brands/cih_light.svg',
    footerWideLogoImage: 'https://media.concierge-suite.com/artifacts/brands/cih_light.svg',
    facebookLink: '',
    instagramLink: '',
    youtubeLink: '',
    twitterLink: '',
    helpdeskLabel: '',
    helpdeskPhone: '',
    helpdeskPhoneHint: null,
    helpdeskEmail: '',
    copyright: `© ${year} Compass International Holdings. All rights reserved. `,
  },
} satisfies Record<string, BrandData>;

@Injectable({ providedIn: 'root' })
export class BrandService {
  private readonly userService = inject(UserService);
  private readonly user = toSignal(this.userService.currentUser);
  private readonly featureFlagService = inject(LaunchDarklyService);
  private readonly theStudioEnabled = toSignal(
    this.featureFlagService.isFeatureEnabled$(FeatureFlags.THE_STUDIO),
  );

  readonly brandCode = computed<BrandCode>(() => {
    const user = this.user();
    const issuer = user?.identityIssuer;
    if (issuer?.includes('compass')) return 'CMP';
    // Users without a brand assigned to their office/company default to the Compass logo.
    if (AppService.isLCAgentApp || AppService.isDCUserApp) {
      return user?.profile?.office?.company?.brandCode ?? 'CIH';
    } else {
      return 'CIH';
    }
  });

  readonly isTheStudio = computed(() => {
    if (!this.theStudioEnabled()) {
      return false;
    }
    return AppService.isLCAgentApp && !(this.brandCode() === 'CBA' || this.brandCode() === 'CBR');
  });

  readonly applicationName = computed<string>(
    () => (this.isTheStudio() ? 'The Studio' : AppService.productSuiteName),
  );

  readonly userSettings = toSignal(inject(UserSettingsService).getCurrentUserSettings$());

  readonly darkTheme = computed(() => this.userSettings()?.isDarkTheme ?? false);

  readonly brandLogo = computed<BrandLogo>(() => {
    const logoScheme = this.isTheStudio() ? logos.theStudio : logos[this.brandCode()] ?? logos.CBA;
    return {
      src: this.darkTheme() && logoScheme.dark ? logoScheme.dark : logoScheme.light,
      width: logoScheme.width,
      height: logoScheme.height,
    };
  });

  readonly supportContact = computed<SupportContact>(() => {
    if (this.isTheStudio()) {
      return {
        phoneNumber: '1.833.547.8266',
        email: 'lc@listing-concierge.com',
      };
    }
    if (AppService.isDCApp) {
      return { email: '', phoneNumber: '833.33.BE.YOU' };
    }
    return {
      phoneNumber: '1.833.LIST.CON',
      email: 'lc@listing-concierge.com',
    };
  });

  readonly brandData = computed<BrandData>(() => brandData[this.brandCode()]);

  constructor() {
    const cookieService = inject(CookieService);

    effect(() => {
      cookieService.setCookie('app', this.applicationName());
    });

    effect(() => {
      if (AppService.isLCAgentApp) {
        const favicon = document.querySelector<HTMLLinkElement>('link[rel=icon]');
        if (favicon) {
          favicon.href = this.isTheStudio() ? 'favicon.ico?version=2' : 'lc-favicon.ico?version=2';
        }
      }
    });
  }
}
